import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sun, Battery, Zap, PlugZap, CheckCircle2, Award } from 'lucide-react';
import { translateHomeType, translateBuildPeriod } from '@/utils/translations';
import ReportDisclaimer from './ReportDisclaimer';
import MeasureProducts from './MeasureProducts';
import StandaloneProducts from './StandaloneProducts';
import InstallersSection from './InstallersSection';
import QuoteRequestCTA from './QuoteRequestCTA';
import useImpressionTracker from '@/hooks/useImpressionTracker';

const MEASURE_TO_PRODUCT_CAT = {
  insulation: 'insulation', glazing: 'glazing', heating: 'heat_pumps',
  ventilation: 'ventilation', 'quick-win': 'small_measures', solar: 'solar', batteries: 'batteries',
};

// Curated execution/installation companies per category — who can do the work
const CATEGORY_PROVIDERS = {
  nl: {
    insulation: [
      { name: 'Essent Woningisolatie', desc: 'Isolatie inclusief subsidieadvies en installatie' },
      { name: 'Vattenfall Energiebesparing', desc: 'Spouw-, dak- en vloerisolatie via gecertificeerde vakmensen' },
      { name: 'Puurisolatie', desc: 'Specialist in na-isolatie van bestaande woningen' },
    ],
    glazing: [
      { name: 'Select Windows', desc: 'Landelijk netwerk voor kozijnen en HR++ beglazing' },
      { name: 'Velux (dakramen)', desc: 'Specialist in dakraam-vervanging en isolerend glas' },
      { name: 'Lokale glaszetter', desc: 'Via uw gemeente of Verbouwcoach.nl' },
    ],
    heat_pumps: [
      { name: 'Feenstra', desc: 'Landelijk installateur, warmtepompen en verwarming' },
      { name: 'Eneco Warmtepompen', desc: 'Full-service installatie via groot energiebedrijf' },
      { name: 'Vattenfall Warmtepomp', desc: 'Installatie met financieringsopties' },
    ],
    boilers: [
      { name: 'Feenstra', desc: 'Landelijke installatie en onderhoud verwarmingsketels' },
      { name: 'Eneco Verwarming', desc: 'HR-ketel installatie via energieleverancier' },
      { name: 'Lokale CV-installateur', desc: 'Via Installatie.nl of uw gemeente' },
    ],
    ventilation: [
      { name: 'Brink Climate Systems', desc: 'Ventilatie-installatie en onderhoud, NL-specialist' },
      { name: 'Feenstra Klimaat', desc: 'Klimaat- en ventilatiesystemen, landelijk' },
      { name: 'Energiewacht Groep', desc: 'Installatie en service energiesystemen' },
    ],
  },
  en: {
    insulation: [
      { name: 'Essent Home Insulation', desc: 'Insulation with subsidy advice and installation' },
      { name: 'Vattenfall Energy Savings', desc: 'Cavity, roof and floor insulation via certified professionals' },
      { name: 'Puurisolatie', desc: 'Specialist in retrofit insulation' },
    ],
    glazing: [
      { name: 'Select Windows', desc: 'National network for frames and HR++ glazing' },
      { name: 'Velux (roof windows)', desc: 'Specialist in roof window replacement' },
      { name: 'Local glazier', desc: 'Via your municipality or Verbouwcoach.nl' },
    ],
    heat_pumps: [
      { name: 'Feenstra', desc: 'National installer, heat pumps and heating' },
      { name: 'Eneco Heat Pumps', desc: 'Full-service installation via major energy company' },
      { name: 'Vattenfall Heat Pump', desc: 'Installation with financing options' },
    ],
    boilers: [
      { name: 'Feenstra', desc: 'National boiler installation and maintenance' },
      { name: 'Eneco Heating', desc: 'HR boiler installation via energy provider' },
      { name: 'Local CV installer', desc: 'Via Installatie.nl or your municipality' },
    ],
    ventilation: [
      { name: 'Brink Climate Systems', desc: 'Ventilation installation and maintenance, NL specialist' },
      { name: 'Feenstra Climate', desc: 'Climate and ventilation systems, nationwide' },
      { name: 'Energiewacht Group', desc: 'Energy systems installation and service' },
    ],
  },
};

const URGENCY_MAP = {
  nl: { high: 'Hoog', medium: 'Gemiddeld', low: 'Laag' },
  en: { high: 'High', medium: 'Medium', low: 'Low' },
};

const CONFIDENCE_MAP = {
  nl: { estimated: 'Geschat', high: 'Hoog', medium: 'Gemiddeld', low: 'Indicatief', likely: 'Waarschijnlijk' },
  en: { estimated: 'Estimated', high: 'High', medium: 'Medium', low: 'Indicative', likely: 'Likely' },
};

function tr(val, map, lang) { return (map[lang] || map.en || {})[val] || val; }

function locVal(val, lang) {
  if (!val || typeof val !== 'string') return val;
  if (lang === 'nl') return val.replace('/year', ' per jaar').replace(/ years /g, ' jaar ').replace(/ years$/, ' jaar').replace(/ year /g, ' jaar ').replace(/ year$/, ' jaar');
  return val;
}

function resolveMeasureTitle(id, measures) {
  const m = measures.find(m => m.id === id);
  return m ? m.title : id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function MeasureCard({ m, t, lang, report, trackImpression, catModels }) {
  const isProduct = m.measure_type === 'product';
  const productCat = MEASURE_TO_PRODUCT_CAT[m.category] || m.category;
  const model = catModels[productCat];

  return (
    <div className="p-5 border rounded-xl bg-white" data-testid={`measure-${m.id}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-base">{m.title}</h3>
        {m.execution_type && (
          <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${isProduct ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
            {m.execution_type}
          </span>
        )}
      </div>

      {/* Impact labels */}
      {m.impact_labels?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {m.impact_labels.map(label => (
            <span key={label} className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${
              label.includes('winst') || label.includes('win') ? 'bg-green-100 text-green-700' :
              label.includes('Comfort') ? 'bg-blue-100 text-blue-700' :
              label.includes('investering') || label.includes('investment') ? 'bg-emerald-100 text-emerald-700' :
              label.includes('effect') || label.includes('impact') ? 'bg-primary/10 text-primary' :
              label.includes('Vakman') || label.includes('Professional') ? 'bg-amber-100 text-amber-700' :
              'bg-purple-100 text-purple-700'
            }`}>{label}</span>
          ))}
        </div>
      )}

      {m.reason && (
        <div className="mb-3">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('report.measureReason')}</p>
          <p className="text-sm text-foreground leading-relaxed">{m.reason}</p>
        </div>
      )}

      {/* Waarom nu / timing */}
      {m.waarom_nu && (
        <div className={`p-2.5 rounded-lg mb-3 text-xs flex items-start gap-2 ${m.timing === 'later' ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
          <span className="font-semibold flex-shrink-0">{m.timing === 'later' ? (lang === 'nl' ? 'Later doen:' : 'Do later:') : (lang === 'nl' ? 'Waarom nu:' : 'Why now:')}</span>
          <span>{m.waarom_nu}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {m.estimated_savings && (
          <div className="p-2.5 bg-green-50 rounded-lg">
            <p className="text-[10px] text-muted-foreground">{t('report.estSavings')}</p>
            <p className="text-sm font-semibold text-green-700">{locVal(m.estimated_savings, lang)}</p>
          </div>
        )}
        {m.price_range && (
          <div className="p-2.5 bg-muted rounded-lg">
            <p className="text-[10px] text-muted-foreground">{t('report.investmentRange')}</p>
            <p className="text-sm font-semibold">{m.price_range}</p>
          </div>
        )}
        {m.payback && (
          <div className="p-2.5 bg-muted rounded-lg">
            <p className="text-[10px] text-muted-foreground">{t('report.payback')}</p>
            <p className="text-sm font-semibold">{locVal(m.payback, lang)}</p>
          </div>
        )}
        {m.execution_type && (
          <div className="p-2.5 bg-muted rounded-lg">
            <p className="text-[10px] text-muted-foreground">{t('report.executionType')}</p>
            <p className="text-sm font-semibold">{m.execution_type}</p>
          </div>
        )}
      </div>

      {m.benefit && (
        <div className="p-3 bg-primary/5 rounded-lg mb-3 flex items-start gap-2">
          <Zap className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[10px] text-primary font-semibold uppercase tracking-wider">{t('report.practicalBenefit')}</p>
            <p className="text-sm text-foreground">{m.benefit}</p>
          </div>
        </div>
      )}

      {/* Products for small/DIY measures */}
      {isProduct && m.products?.length > 0 && (
        <MeasureProducts products={m.products} reportId={report.report_id} country={report.country} region={report.user_region} onImpression={trackImpression} />
      )}

      {/* Partners + Quote CTA for large/professional measures */}
      {!isProduct && (
        <>
          {(() => {
            const providers = (CATEGORY_PROVIDERS[lang] || CATEGORY_PROVIDERS.en)[productCat] || [];
            return providers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50 mb-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {lang === 'nl' ? 'Uitvoerende partijen in uw regio' : 'Installation companies in your area'}
                </p>
                <div className="space-y-1.5">
                  {providers.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
                      <span className="w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground text-xs">— {p.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {(model === 'lead' || model === 'hybrid' || !model || m.measure_type === 'quote') && (
            <QuoteRequestCTA category={productCat} reportId={report.report_id} country={report.country} region={report.user_region} postcode={report.user_postcode} email={report.user_email} report={report} />
          )}
        </>
      )}
    </div>
  );
}

function SolarSection({ solar, t, lang, report }) {
  if (!solar || !solar.show) return null;

  const providers = lang === 'nl' ? [
    { name: 'Zonneplan', desc: 'Landelijk actief, all-in pakketten' },
    { name: 'Solarwatt', desc: 'Duitse kwaliteitspanelen, lokale installateurs' },
    { name: 'Engie Zonnepanelen', desc: 'Groot energiebedrijf, financieringsopties' },
  ] : [
    { name: 'Zonneplan', desc: 'Nationwide, all-in packages' },
    { name: 'Solarwatt', desc: 'German quality panels, local installers' },
    { name: 'Engie Solar', desc: 'Major energy company, financing options' },
  ];

  return (
    <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="solar-section">
      <div className="flex items-center gap-2 mb-4">
        <Sun className="w-5 h-5 text-amber-500" />
        <h2 className="text-xl font-medium text-primary" style={{ fontFamily: 'Outfit' }}>{solar.title}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4 italic">{solar.suitability}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="p-3 bg-amber-50 rounded-lg">
          <p className="text-[10px] text-muted-foreground">{t('report.investmentRange')}</p>
          <p className="text-sm font-semibold">{solar.investment}</p>
        </div>
        <div className="p-3 bg-amber-50 rounded-lg">
          <p className="text-[10px] text-muted-foreground">{t('report.payback')}</p>
          <p className="text-sm font-semibold">{solar.payback}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-[10px] text-muted-foreground">{solar.system_size?.split(':')[0]}</p>
          <p className="text-sm font-semibold">{solar.system_size?.split(': ').pop()}</p>
        </div>
      </div>
      <div className="space-y-1 text-sm text-foreground mb-4">
        <p>{solar.production}</p>
        <p>{solar.coverage}</p>
      </div>
      <div className="p-3 bg-primary/5 rounded-lg mb-4 flex items-start gap-2">
        <Zap className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-sm">{solar.benefit}</p>
      </div>

      {/* Providers + Quote CTA */}
      <div className="mt-3 pt-3 border-t border-border/50 mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {lang === 'nl' ? 'Aanbevolen specialisten' : 'Recommended specialists'}
        </p>
        <div className="space-y-1.5">
          {providers.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
              <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <span className="font-medium">{p.name}</span>
              <span className="text-muted-foreground text-xs">— {p.desc}</span>
            </div>
          ))}
        </div>
      </div>
      <QuoteRequestCTA category="solar" reportId={report.report_id} country={report.country} region={report.user_region} postcode={report.user_postcode} email={report.user_email} report={report} />
    </div>
  );
}

function BatterySection({ battery, t, lang, report }) {
  if (!battery || !battery.show) return null;
  const relevanceColor = battery.relevance === 'relevant' ? 'bg-green-50 border-green-200' :
    battery.relevance === 'later' ? 'bg-amber-50 border-amber-200' : 'bg-muted border-border';
  const relevanceLabel = battery.relevance === 'relevant' ? (lang === 'nl' ? 'Kansrijk' : 'Promising') :
    battery.relevance === 'later' ? (lang === 'nl' ? 'Later overwegen' : 'Consider later') :
    (lang === 'nl' ? 'Nog geen prioriteit' : 'Not a priority yet');

  const providers = lang === 'nl' ? [
    { name: 'Tesla Powerwall', desc: 'Bekendste thuisbatterij, bewezen technologie' },
    { name: 'BYD Battery-Box', desc: 'Modulair systeem, schaalbaar' },
    { name: 'Enphase IQ Battery', desc: 'Compact, goed combineerbaar met zonnepanelen' },
  ] : [
    { name: 'Tesla Powerwall', desc: 'Most well-known home battery, proven technology' },
    { name: 'BYD Battery-Box', desc: 'Modular system, scalable' },
    { name: 'Enphase IQ Battery', desc: 'Compact, integrates well with solar panels' },
  ];

  return (
    <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="battery-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Battery className="w-5 h-5 text-blue-500" />
          <h2 className="text-xl font-medium text-primary" style={{ fontFamily: 'Outfit' }}>{battery.title}</h2>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full font-medium border ${relevanceColor}`}>{relevanceLabel}</span>
      </div>
      <p className="text-sm text-foreground mb-4">{battery.advice}</p>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-[10px] text-muted-foreground">{t('report.investmentRange')}</p>
          <p className="text-sm font-semibold">{battery.investment}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-[10px] text-muted-foreground">{t('report.payback')}</p>
          <p className="text-sm font-semibold">{battery.payback}</p>
        </div>
      </div>
      <div className="p-3 bg-primary/5 rounded-lg flex items-start gap-2">
        <Zap className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-sm">{battery.benefit}</p>
      </div>

      {/* Commercial next step — only when relevant or later */}
      {battery.relevance !== 'not_priority' && (
        <>
          <div className="mt-3 pt-3 border-t border-border/50 mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {lang === 'nl' ? 'Aanbevolen oplossingen' : 'Recommended solutions'}
            </p>
            <div className="space-y-1.5">
              {providers.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
                  <span className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                  <span className="font-medium">{p.name}</span>
                  <span className="text-muted-foreground text-xs">— {p.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <QuoteRequestCTA category="batteries" reportId={report.report_id} country={report.country} region={report.user_region} postcode={report.user_postcode} email={report.user_email} report={report} />
        </>
      )}
    </div>
  );
}

function ChargerSection({ charger, lang, report }) {
  if (!charger || !charger.show) return null;

  const isRelevant = charger.relevance === 'relevant';

  const providers = lang === 'nl' ? [
    { name: 'NewMotion / Shell Recharge', desc: 'Landelijk netwerk, installatie en beheer' },
    { name: 'Vattenfall Laadpalen', desc: 'Installatie via energieleverancier' },
    { name: 'ANWB Thuisladen', desc: 'Betrouwbaar, inclusief installatie en service' },
  ] : [
    { name: 'NewMotion / Shell Recharge', desc: 'National network, installation and management' },
    { name: 'Vattenfall Chargers', desc: 'Installation via energy provider' },
    { name: 'ANWB Home Charging', desc: 'Reliable, includes installation and service' },
  ];

  return (
    <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="charger-section">
      <div className="flex items-center gap-2 mb-4">
        <PlugZap className="w-5 h-5 text-emerald-600" />
        <h2 className="text-xl font-medium text-primary" style={{ fontFamily: 'Outfit' }}>{charger.title}</h2>
      </div>

      <p className="text-sm text-foreground mb-4 leading-relaxed">{charger.explanation}</p>

      {isRelevant && charger.benefits?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-emerald-700 mb-2">{lang === 'nl' ? 'Wat dit oplevert:' : 'What this delivers:'}</p>
          <ul className="space-y-1.5">
            {charger.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isRelevant && charger.investment && (
        <div className="p-3 bg-emerald-50 rounded-lg mb-4">
          <p className="text-[10px] text-muted-foreground">{lang === 'nl' ? 'Kosten' : 'Cost'}</p>
          <p className="text-sm font-semibold">{lang === 'nl' ? 'Op aanvraag via installateur' : 'Quote on request via installer'}</p>
        </div>
      )}

      {charger.suitability && (
        <p className="text-xs text-muted-foreground italic mb-4">{charger.suitability}</p>
      )}

      {/* Providers + Quote CTA — only when relevant */}
      {isRelevant && (
        <>
          <div className="mt-3 pt-3 border-t border-border/50 mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {lang === 'nl' ? 'Uitvoerende partijen' : 'Installation companies'}
            </p>
            <div className="space-y-1.5">
              {providers.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
                  <span className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                  <span className="font-medium">{p.name}</span>
                  <span className="text-muted-foreground text-xs">— {p.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <QuoteRequestCTA category="ev_charger" reportId={report.report_id} country={report.country} region={report.user_region} postcode={report.user_postcode} email={report.user_email} report={report} chargerTerrain={charger.terrain_answer} />
        </>
      )}
    </div>
  );
}

export default function FullReport({ report }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { trackImpression } = useImpressionTracker(report.report_id, report.country, report.user_region);
  const catModels = report.category_models || {};
  const allMeasures = report.recommended_measures || [];

  // Split measures by type
  const quoteMeasures = allMeasures.filter(m => m.measure_type !== 'product');
  const productMeasures = allMeasures.filter(m => m.measure_type === 'product');

  return (
    <div className="space-y-6" data-testid="full-report">
      {/* Executive Summary */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="executive-summary">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.executiveSummary')}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted rounded-lg" data-testid="summary-savings">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('report.savingsDirection')}</span>
            <p className="text-2xl font-medium text-foreground mt-1">{report.estimated_savings.direction || (lang === 'nl' ? 'Verwacht in uw situatie' : 'Expected in your situation')}</p>
            <p className="text-xs text-muted-foreground mt-1">{tr(report.estimated_savings.confidence, CONFIDENCE_MAP, lang)}</p>
            <p className="text-[11px] text-muted-foreground/80 mt-1.5 leading-snug italic">
              {lang === 'nl' ? 'Concrete bedragen volgen na koppeling van uw apparaten.' : 'Concrete amounts follow after connecting your devices.'}
            </p>
          </div>
          <div className="p-4 bg-muted rounded-lg" data-testid="summary-co2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('report.co2Reduction')}</span>
            <p className="text-2xl font-medium text-foreground mt-1">{report.estimated_savings.co2_reduction_kg} kg/{lang === 'nl' ? 'jaar' : 'year'}</p>
          </div>
          <div className="p-4 bg-muted rounded-lg" data-testid="summary-complexity">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('report.complexity')}</span>
            <p className="text-2xl font-medium text-foreground mt-1">{report.urgency_scores.complexity}/10</p>
          </div>
        </div>
      </div>

      {/* Home Profile */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="home-profile">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.homeProfile')}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { label: t('report.homeType'), value: translateHomeType(report.home_profile.home_type, t) },
            { label: t('report.buildPeriod'), value: translateBuildPeriod(report.home_profile.build_period, t) },
            { label: t('report.size'), value: report.home_profile.size },
            { label: t('report.energyLabel'), value: report.home_profile.energy_label },
          ].map((item) => (
            <div key={item.label}><p className="text-sm text-muted-foreground mb-1">{item.label}</p><p className="font-medium">{item.value}</p></div>
          ))}
        </div>
        {/* Home analysis summary */}
        {report.home_summary && (
          <div className="grid md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-border/50">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{lang === 'nl' ? 'Grootste verliespost' : 'Biggest heat loss'}</p>
              <p className="text-sm font-medium mt-0.5">{report.home_summary.biggest_loss}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{lang === 'nl' ? 'Grootste kans' : 'Biggest opportunity'}</p>
              <p className="text-sm font-medium mt-0.5">{report.home_summary.biggest_chance}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{lang === 'nl' ? 'Aanbevolen eerste stap' : 'Recommended first step'}</p>
              <p className="text-sm font-medium mt-0.5 text-primary">{report.home_summary.recommended_first_step}</p>
            </div>
          </div>
        )}
      </div>

      {/* Priorities */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="priorities-section">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.topPriorities')}</h2>
        <div className="space-y-3">
          {report.priorities.map((p) => (
            <div key={`pr-${p.rank}`} className="p-4 border-l-4 border-primary bg-muted rounded-r-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold flex-shrink-0">{p.rank}</div>
                <div>
                  <h3 className="font-semibold">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                  <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${p.urgency === 'high' ? 'bg-destructive/10 text-destructive' : 'bg-secondary/10 text-secondary'}`}>
                    {tr(p.urgency, URGENCY_MAP, lang)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Large / quote-type measures */}
      {quoteMeasures.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="measures-section">
          <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.recommendedMeasures')}</h2>
          <div className="space-y-4">
            {quoteMeasures.map((m) => (
              <MeasureCard key={m.id} m={m} t={t} lang={lang} report={report} trackImpression={trackImpression} catModels={catModels} />
            ))}
          </div>
        </div>
      )}

      {/* Small / product-type measures */}
      {productMeasures.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="product-measures-section">
          <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>
            {lang === 'nl' ? 'Directe verbeteringen — zelf te doen' : 'Quick improvements — DIY'}
          </h2>
          <div className="space-y-4">
            {productMeasures.map((m) => (
              <MeasureCard key={m.id} m={m} t={t} lang={lang} report={report} trackImpression={trackImpression} catModels={catModels} />
            ))}
          </div>
        </div>
      )}

      {/* Solar section */}
      <SolarSection solar={report.solar_section} t={t} lang={lang} report={report} />

      {/* Battery section */}
      <BatterySection battery={report.battery_section} t={t} lang={lang} report={report} />

      {/* Charger section */}
      <ChargerSection charger={report.charger_section} lang={lang} report={report} />

      {/* Do Now / Later / Avoid */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="action-plan">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.actionPlan')}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-800 mb-2">{t('report.doNow')}</h3>
            {report.do_now?.length > 0 ? (
              <ul className="space-y-1">{report.do_now.map((id) => <li key={id} className="text-sm text-green-700">• {resolveMeasureTitle(id, allMeasures)}</li>)}</ul>
            ) : <p className="text-sm text-green-600">{t('report.noItems')}</p>}
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="font-semibold text-amber-800 mb-2">{t('report.doLater')}</h3>
            {report.do_later?.length > 0 ? (
              <ul className="space-y-1">{report.do_later.map((id) => <li key={id} className="text-sm text-amber-700">• {resolveMeasureTitle(id, allMeasures)}</li>)}</ul>
            ) : <p className="text-sm text-amber-600">{t('report.noItems')}</p>}
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2">{t('report.avoid')}</h3>
            {report.avoid?.length > 0 ? (
              <ul className="space-y-1">{report.avoid.map((msg, i) => <li key={`avoid-${i}`} className="text-sm text-red-700">• {msg}</li>)}</ul>
            ) : <p className="text-sm text-red-600">{t('report.noItems')}</p>}
          </div>
        </div>
      </div>

      {/* Energielabel guidance */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="energielabel-section">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-primary" />
          <h2 className="text-2xl font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
            {lang === 'nl' ? 'Welke stappen kunnen helpen richting een beter energielabel?' : 'Which steps may help toward a better energy label?'}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          {lang === 'nl'
            ? 'Een energiezuinigere woning kan gunstig zijn voor uw energierekening, wooncomfort en mogelijk ook voor de waarde of verkoopbaarheid van uw woning. Onderstaande maatregelen kunnen in uw situatie logisch zijn richting een beter energielabel.'
            : 'A more energy-efficient home can benefit your energy bill, living comfort, and potentially your home\'s value. The measures below may logically help toward a better energy label in your situation.'}
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { id: 'insulation', nl: 'Isolatie (dak, muur, vloer)', en: 'Insulation (roof, wall, floor)' },
            { id: 'glazing', nl: 'HR++ of triple glas', en: 'HR++ or triple glazing' },
            { id: 'ventilation', nl: 'Ventilatie met warmteterugwinning', en: 'Ventilation with heat recovery' },
            { id: 'solar', nl: 'Zonnepanelen', en: 'Solar panels' },
            { id: 'heating', nl: 'Warmtepomp of hybride verwarmingssysteem', en: 'Heat pump or hybrid heating system' },
            { id: 'controls', nl: 'Slimme sturing en thermostaatregeling', en: 'Smart controls and thermostat management' },
          ].map(item => (
            <div key={item.id} className="flex items-center gap-2 text-sm bg-muted/40 rounded-lg px-3 py-2">
              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
              <span className="text-foreground">{lang === 'nl' ? item.nl : item.en}</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-3 italic">
          {lang === 'nl'
            ? 'Dit overzicht is een indicatie. Een officieel energielabel wordt bepaald door een gecertificeerde EPA-adviseur.'
            : 'This overview is indicative. An official energy label is determined by a certified energy advisor.'}
        </p>
      </div>

      {/* Budget Scenarios */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="budget-scenarios">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.budgetScenarios')}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {report.budget_scenarios.map((s) => {
            const BUDGET_NAME_MAP = { 'Quick Wins': 'Directe verbeteringen', 'Essential': 'Essentieel', 'Comprehensive': 'Volledig' };
            const displayName = lang === 'nl' ? (BUDGET_NAME_MAP[s.name] || s.name) : s.name;
            return (
              <div key={s.name} className="p-4 border rounded-lg">
                <h3 className="font-semibold text-lg mb-1">{displayName}</h3>
                <p className="text-2xl font-light text-primary" style={{ fontFamily: 'Outfit' }}>{s.budget}</p>
                <p className="text-sm text-muted-foreground mt-2">{s.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Budget Paths — practical helper */}
      {report.budget_paths?.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="budget-paths">
          <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>
            {lang === 'nl' ? 'Beperkt budget? Begin hier' : 'Limited budget? Start here'}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {report.budget_paths.map(bp => (
              <div key={bp.label} className="p-4 border rounded-lg">
                <h3 className="font-semibold text-primary mb-2">{bp.label}</h3>
                {bp.measures.length > 0 ? (
                  <ul className="space-y-1">
                    {bp.measures.map((m, i) => <li key={`bp-${i}`} className="text-sm text-foreground">• {m}</li>)}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground italic">{lang === 'nl' ? 'Geen maatregelen in dit budget' : 'No measures in this budget'}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subsidies */}
      {report.subsidies?.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-border shadow-sm" data-testid="subsidies-section">
          <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.subsidies')}</h2>
          <div className="space-y-3">
            {report.subsidies.map((s, i) => (
              <div key={`sub-${i}`} className="p-4 border rounded-lg">
                <h3 className="font-medium mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">{s.summary}</p>
                {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{s.source_name}</a>}
                <p className="text-xs text-muted-foreground mt-2 italic">{s.disclaimer}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">{t('report.subsidyDisclaimer')}</p>
          </div>
        </div>
      )}

      <StandaloneProducts products={report.standalone_products} reportId={report.report_id} country={report.country} region={report.user_region} onImpression={trackImpression} />
      <InstallersSection localInstallers={report.local_installers} otherInstallers={report.other_installers} userRegion={report.user_region} installers={report.installers} />
      <ReportDisclaimer />
    </div>
  );
}
